import React, { useState } from 'react';
import { TextField } from '@mui/material';

// Valid Indian mobile numbers: exactly 10 digits, starting with 6, 7, 8, or 9.
const isValidMobileNo = (mobileNo: string): boolean => /^[6-9]\d{9}$/.test(mobileNo);

type MobileNumberFieldProps = {
  label: string;
  name?: string;
  value: string | null; // The phone number as a string or null for empty.
  onChange: (value: string | null) => void; // Function to handle value changes.
  required?: boolean;
  fullWidth?: boolean;
  width?: string; // Optional width for the input.
};

// A phone number is optional unless `required` — but if the user enters anything, it must
// be a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9). Error only shows
// after the field is left (blur), so it doesn't flash red while the user is still mid-typing.
export const MobileNumberField: React.FC<MobileNumberFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  fullWidth,
  width,
}) => {
  const [touched, setTouched] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only digits
    if (!/^\d*$/.test(inputValue)) return;

    // Max 10 digits
    if (inputValue.length > 10) return;

    // Valid Indian mobile numbers start with 6, 7, 8, or 9 — reject the keystroke that
    // would make the first digit anything else, same way a non-digit keystroke is rejected.
    if (inputValue.length > 0 && !/^[6-9]/.test(inputValue)) return;

    onChange(inputValue === "" ? null : inputValue);
  };

  const isEmpty = !value;
  const missingRequired = required && isEmpty;
  const invalidFormat = !isEmpty && !isValidMobileNo(value as string);
  const hasError = touched && (missingRequired || invalidFormat);
  const errorText = missingRequired
    ? `${label} is required`
    : invalidFormat
      ? required
        ? `Enter a valid 10-digit ${label} starting with 6-9`
        : `Enter a valid 10-digit ${label} starting with 6-9, or leave it empty`
      : undefined;

  // The rule is: a full valid 10-digit number, or nothing at all — never a partial/invalid
  // one left sitting in the field. So an invalid (non-empty) value isn't allowed to keep
  // focus once the user tries to leave; blur re-traps focus until it's fixed or cleared.
  // The re-focus is deferred a tick so it wins over whatever just took focus away.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    if (invalidFormat) {
      const el = e.target;
      setTimeout(() => {
        if (el.isConnected) {
          el.focus();
          el.select();
        }
      }, 0);
    }
  };

  return (
    <TextField
      label={label}
      name={name}
      value={value ?? ""} // Display empty string if value is null
      onChange={handleChange}
      onBlur={handleBlur}
      required={required}
      error={hasError}
      helperText={hasError ? errorText : undefined}
      size="small"
      fullWidth={fullWidth}
      sx={width ? { width } : undefined}
      slotProps={{
        htmlInput: {
          inputMode: 'numeric', // Numeric keypad on mobile devices
          maxLength: 10,        // hard stop at 10 digits
          pattern: '[6-9][0-9]{9}', // native backstop: skipped when empty, enforced when filled
        },
      }}
    />
  );
};
